import { useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { createExportRun } from "../api/requests/runs.requests";
import { QUERY_KEYS } from "../api/config";
import {
  chunkImageIds,
  HARD_MAX_IMAGES_PER_RUN,
  type RunCreateInput,
} from "../types/run";

export type ChunkStatus = "pending" | "in_progress" | "success" | "failed";

export interface ChunkState {
  index: number;
  /** Images in this chunk. */
  count: number;
  status: ChunkStatus;
  error?: string;
  /** Set once the run for this chunk was created. */
  runId?: string;
}

interface UseChunkedExportRunResult {
  chunks: ChunkState[];
  isRunning: boolean;
  /** Split `input.image_ids` into runs of `chunkSize` and create them in order. */
  execute: (input: RunCreateInput, chunkSize: number) => Promise<boolean>;
  retryFromFailed: () => Promise<boolean>;
  reset: () => void;
}

/**
 * A single POST /runs/ is capped at `images_max_per_run` (200), so an over-cap
 * selection is created as N sequential runs. Mirrors `useBatchChunkedReview`:
 * same result shape, same per-chunk status, stops on the first failure so a
 * retry can resume from exactly there without re-firing successful runs.
 */
export const useChunkedExportRun = (): UseChunkedExportRunResult => {
  const { t } = useTranslation("mlExports");
  const queryClient = useQueryClient();
  const [chunks, setChunks] = useState<ChunkState[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const inputRef = useRef<RunCreateInput | null>(null);
  const chunkSizeRef = useRef<number>(HARD_MAX_IMAGES_PER_RUN);
  const startedRunsRef = useRef<number>(0);

  const updateChunk = (index: number, update: Partial<ChunkState>) => {
    setChunks((prev) =>
      prev.map((c) => (c.index === index ? { ...c, ...update } : c)),
    );
  };

  const invalidateHistory = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RUNS_HISTORY] });
  }, [queryClient]);

  const runFrom = useCallback(
    async (
      input: RunCreateInput,
      chunkSize: number,
      startFrom: number,
    ): Promise<boolean> => {
      const batches = chunkImageIds(input.image_ids, chunkSize);

      if (startFrom === 0) {
        setChunks(
          batches.map((batch, i) => ({
            index: i,
            count: batch.length,
            status: "pending" as ChunkStatus,
          })),
        );
        startedRunsRef.current = 0;
      }

      setIsRunning(true);
      const startedBefore = startedRunsRef.current;
      let success = true;

      for (let i = startFrom; i < batches.length; i++) {
        updateChunk(i, { status: "in_progress" });

        try {
          const run = await createExportRun({
            ...input,
            image_ids: batches[i],
          });
          startedRunsRef.current += 1;
          updateChunk(i, { status: "success", runId: run?.run_id });
        } catch (err: unknown) {
          const apiError = (err as {
            response?: { data?: { error?: { detail?: string } } };
          })?.response?.data?.error;

          updateChunk(i, {
            status: "failed",
            error: apiError?.detail ?? t("chunkProgress.runFailed"),
          });
          success = false;
          break;
        }
      }

      setIsRunning(false);
      // Every run that was created is already in history — refresh even when a
      // later chunk failed, unlike the review batch which is all-or-nothing.
      if (startedRunsRef.current > startedBefore) invalidateHistory();
      return success;
    },
    [invalidateHistory, t],
  );

  const execute = useCallback(
    async (input: RunCreateInput, chunkSize: number): Promise<boolean> => {
      const size = Math.min(
        Number.isFinite(chunkSize) && chunkSize > 0
          ? chunkSize
          : HARD_MAX_IMAGES_PER_RUN,
        HARD_MAX_IMAGES_PER_RUN,
      );
      inputRef.current = input;
      chunkSizeRef.current = size;
      return runFrom(input, size, 0);
    },
    [runFrom],
  );

  const retryFromFailed = useCallback(async (): Promise<boolean> => {
    const failedIndex = chunks.findIndex((c) => c.status === "failed");
    if (failedIndex === -1 || !inputRef.current) return true;
    return runFrom(inputRef.current, chunkSizeRef.current, failedIndex);
  }, [chunks, runFrom]);

  const reset = useCallback(() => {
    setChunks([]);
    setIsRunning(false);
    inputRef.current = null;
    chunkSizeRef.current = HARD_MAX_IMAGES_PER_RUN;
    startedRunsRef.current = 0;
  }, []);

  return { chunks, isRunning, execute, retryFromFailed, reset };
};
